import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PreviewCard,
  LoadingCard,
  Loader,
  MainButton,
  InputField,
  IconWrapper,
  BodyContainer,
  PrimaryHeading,
  HeaderContainer,
  ColorPicker,
  RightMainContainer,
  DeleteButton,
} from "commonUI";
import { BackArrow, SaveIcon } from "allIcons";
import {
  getUserSavePermission,
  isEqual,
  titleCase,
  useSearch,
  useUtilities,
} from "helpers";
import {
  useAddPriority,
  useDeletePriority,
  usePriority,
  useUpdatePriority,
} from "hooks/priority";
import { useUserPermissions } from "hooks/user";
import { t } from "i18next";
import { ModulePrefixes } from "enums";

export default function PrioritySetting() {
  const { notification } = useUtilities();
  const navigate = useNavigate();
  const { web = null } = useSearch();
  const { isLoading, data: priorities } = usePriority();
  let { id } = useParams();
  let priority = priorities.filter((element) => isEqual(element.id, id))[0] ?? {};
  const [value, setValue] = useState(priority?.color ?? "#0066ff");
  const [inputValue, setInputValue] = useState(
    titleCase(priority?.priority_title ?? "")
  );
  const {
    add_new_items_to_site_template,
    remove_items_from_site_template,
    edit_item_details_in_site_template,
  } = useUserPermissions();
  useEffect(() => {
    if (!isEqual(id, "add") && !isLoading) {
      let priority = priorities.filter((element) => isEqual(element.id, id))[0] ?? {};
      if (!priority?.id) {
        navigate(`/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/priority${web ? `?web=${web}` : ""}`);
        return;
      }
      setValue(priority?.color);
      setInputValue(priority?.priority_title);
    }
  }, [isLoading, priorities, id, web, navigate]);

  const addMutation = useAddPriority(`/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/priority/`);
  const updateMutation = useUpdatePriority();
  const deleteMutation = useDeletePriority(
    `/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/priority${web ? `?web=${web}` : ""}`
  );

  function handleSaveChanges() {
    if (!inputValue || !value || isEqual(value, '#')) {
      notification({
        type: "error",
        message: inputValue ? t("siteSetting.priority.input.error.enterColor") : t("siteSetting.priority.input.error.enterTitle")
      });
      return;
    }
    if (isEqual(id, "add")) {
      addMutation.mutate({
        priority_title: inputValue,
        color: value,
        website_id: web,
      });
    } else {
      updateMutation.mutate({
        priority_title: inputValue,
        color: value,
        priority_id: id,
        website_id: web,
      });
    }
  }

  return (
    <>
      <RightMainContainer>
        {(addMutation.isLoading ||
          updateMutation.isLoading ||
          deleteMutation.isLoading) && <Loader loader="block" />}
        <HeaderContainer>
          <IconWrapper>
            <BackArrow
              onClick={() =>
                navigate(`/${web ? ModulePrefixes.SITE_SETTINGS : ModulePrefixes.SITE_TEMPLATE}/priority${web ? `?web=${web}` : ""}`)
              }
            />
            {isLoading ? (
              <LoadingCard className="w-40 h-6" />
            ) : (
              <PrimaryHeading>{inputValue ?? t("siteSetting.priority.addPriority")}</PrimaryHeading>
            )}
          </IconWrapper>
          <div className="flex items-center justify-center gap-14">
            <DeleteButton
              show={!isEqual(id, "add") && remove_items_from_site_template && web}
              deleteMessage={
                web
                  ? t("siteSetting.priority.confirmDelete.forSite.message")
                  : t("siteSetting.priority.confirmDelete.message")
              }
              deleteTitle={
                web ? t("siteSetting.priority.confirmDelete.forSite.title") : t("siteSetting.priority.confirmDelete.title")
              }
              deleteButtonText={web ? t("common.popup.button.confirmDelete") : t("common.deleteButton.title")}
              onDelete={() => deleteMutation.mutate({ id })}
              isDeleteButton={!!web}
            />
            {getUserSavePermission({
              id,
              addPermission: add_new_items_to_site_template,
              editPermission: edit_item_details_in_site_template,
            }) ? (
              <MainButton onClick={handleSaveChanges}>
                <SaveIcon />
                {t("button.saveChanges")}
              </MainButton>
            ) : (
              ""
            )}
          </div>
        </HeaderContainer>
        <BodyContainer>
          <div>
            <InputField
              label={t("siteSetting.priority.input.title.label")}
              placeholder={t("siteSetting.priority.input.title.placeholder")}
              value={inputValue}
              setValue={setInputValue}
              isLoading={isLoading}
            />
          </div>
          <div>
            <PreviewCard
              title={inputValue}
              color={isEqual((value?.[0] ?? ""), "#") ? value : "#" + value ?? ""}
              isLoading={isLoading}
              icon={true}
            />
          </div>
          <div>
            <InputField
              label={t("siteSetting.priority.input.color.label")}
              placeholder={t("siteSetting.priority.input.color.placeholder")}
              value={isEqual((value?.[0] ?? ""), "#") ? value : "#" + value ?? ""}
              setValue={setValue}
              isLoading={isLoading}
            />
          </div>
          <div>
            <ColorPicker
              value={isEqual((value?.[0] ?? ""), "#") ? value : "#" + value ?? ""}
              setValue={setValue}
            />
          </div>
        </BodyContainer>
      </RightMainContainer>
    </>
  );
}
